import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './AdminDashboard.css';

function AdminTenants() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    taxSystem: 'DE',
    address: { street: '', city: '', zip: '', country: '' },
    bankData: { bankName: '', iban: '', bic: '' }
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/admin/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setTenants(data);
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingTenant 
        ? `${config.API_URL || 'https://api.garagedesk.net/api'}/admin/tenants/${editingTenant.id}`
        : `${config.API_URL || 'https://api.garagedesk.net/api'}/admin/tenants`;
      
      const method = editingTenant ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern');
      }

      setShowForm(false);
      setEditingTenant(null);
      setFormData({
        name: '',
        taxSystem: 'DE',
        address: { street: '', city: '', zip: '', country: '' },
        bankData: { bankName: '', iban: '', bic: '' }
      });
      loadTenants();
    } catch (error) {
      console.error('Error saving tenant:', error);
      showNotification('Fehler beim Speichern des Tenants', 'error');
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name || '',
      taxSystem: tenant.taxSystem || 'DE',
      address: tenant.address || { street: '', city: '', zip: '', country: '' },
      bankData: tenant.bankData || { bankName: '', iban: '', bic: '' }
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tenant wirklich löschen?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/admin/tenants/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen');
      }

      loadTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      showNotification('Fehler beim Löschen des Tenants', 'error');
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Tenants verwalten</h1>
        <div className="admin-header-actions">
          <button type="button" onClick={() => navigate('/admin')} className="btn-secondary">
            ← Zurück
          </button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
            Neuer Tenant
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingTenant ? 'Tenant bearbeiten' : 'Neuer Tenant'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Steuersystem</label>
                <select
                  value={formData.taxSystem}
                  onChange={(e) => setFormData({ ...formData, taxSystem: e.target.value })}
                >
                  <option value="DE">Deutschland (19%)</option>
                  <option value="CH">Schweiz (7.7%)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Straße</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>PLZ</label>
                  <input
                    type="text"
                    value={formData.address.zip}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, zip: e.target.value }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Stadt</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Land</label>
                <input
                  type="text"
                  value={formData.address.country}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, country: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>Bankname</label>
                <input
                  type="text"
                  value={formData.bankData.bankName}
                  onChange={(e) => setFormData({
                    ...formData,
                    bankData: { ...formData.bankData, bankName: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>IBAN</label>
                <input
                  type="text"
                  value={formData.bankData.iban}
                  onChange={(e) => setFormData({
                    ...formData,
                    bankData: { ...formData.bankData, iban: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>BIC</label>
                <input
                  type="text"
                  value={formData.bankData.bic}
                  onChange={(e) => setFormData({
                    ...formData,
                    bankData: { ...formData.bankData, bic: e.target.value }
                  })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingTenant ? 'Speichern' : 'Erstellen'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTenant(null);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={tenants}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name || ''
          },
          {
            key: 'taxSystem',
            header: 'Steuersystem',
            sortType: 'string',
            accessor: (row) => row.taxSystem === 'CH' ? 'Schweiz (7.7%)' : 'Deutschland (19%)'
          }
        ]}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="Keine Tenants vorhanden"
      />
    </div>
  );
}

export default AdminTenants;

